import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Observable, Subscription} from 'rxjs';
import {IScheduleState} from '../../store/reducers/schedule.reducer';
import {EventFamily, Tempo, TimeSlot} from '../../store/entities';
import {NgRedux} from '@angular-redux3/store';
import {PlaylistRemoteAppState} from '../../store';
import {ComponentCanDeactivate} from '../../guards/pending-changes.guard';

@Component({
  selector: 'mwo-edit-slot-page',
  templateUrl: './edit-slot-page.component.html',
  styleUrls: ['./edit-slot-page.component.scss']
})
export class EditSlotPageComponent implements OnInit, OnDestroy, ComponentCanDeactivate {

  slotPosition: number;
  activeDay$: Observable<string> = this.ngRedux.select('activeDay');
  schedule$: Observable<IScheduleState> = this.ngRedux.select('schedule');
  activeDay: string;
  slots: TimeSlot[] = [];
  slotToEdit: TimeSlot;
  private subscriptions: Subscription[] = [];
  hasUserMadeChanges: boolean = false;

  private initialPlaylist: EventFamily;
  private initialPlayrule: Tempo;
  private initialStartTime: number[];
  private initialEndTime: number[];
  private initialShuffle: boolean;
  private createSlotMode: boolean = false;

  constructor(private router: Router, private route: ActivatedRoute, private ngRedux: NgRedux<PlaylistRemoteAppState>) {
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['slotPosition']) {
        this.slotPosition = +params['slotPosition'];
        this.createSlotMode = false;
      } else {
        this.createSlotMode = true;
      }
    });

    const activeDaySubscription = this.activeDay$.subscribe((day) => {
      this.activeDay = day;
    });
    this.subscriptions.push(activeDaySubscription);

    const scheduleSubscription = this.schedule$.subscribe((schedule) => {
      this.slots = (schedule[this.activeDay] || {}).slots || [];
    });
    this.subscriptions.push(scheduleSubscription);

    if (!this.createSlotMode) {
      this.slotToEdit = {
        event: this.slots[this.slotPosition].event,
        tempo: this.slots[this.slotPosition].tempo,
        startTime: this.slots[this.slotPosition].startTime,
        endTime: this.slots[this.slotPosition].endTime,
        shuffle: this.slots[this.slotPosition].shuffle
      };
      this.initialPlaylist = this.slotToEdit.event;
      this.initialPlayrule = this.slotToEdit.tempo;
      this.initialStartTime = this.slotToEdit.startTime;
      this.initialEndTime = this.slotToEdit.endTime;
      this.initialShuffle = this.slotToEdit.shuffle;
    } else {
      this.slotToEdit = {
        event: undefined,
        tempo: undefined,
        startTime: [0, 0],
        endTime: [23, 0],
        shuffle: false
      };
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  backToScheduler() {
    if (!this.createSlotMode) {
      this.slotToEdit.event = this.initialPlaylist;
      this.slotToEdit.tempo = this.initialPlayrule;
      this.slotToEdit.startTime = this.initialStartTime;
      this.slotToEdit.endTime = this.initialEndTime;
      this.slotToEdit.shuffle = this.initialShuffle;
    }
    this.router.navigate(['/edit-scheduler-page']);
  }

  handleCancel() {
    if (this.hasUserMadeChanges) {
      if (confirm('WARNING: You have unsaved changes. Press Cancel to go back and save these changes, or OK to lose these changes.')) {
        this.hasUserMadeChanges = false;
        this.backToScheduler();
      }
    } else {
      this.backToScheduler();
    }
  }

  handleValidate() {
    this.hasUserMadeChanges = false;
    this.insertNewSlot(this.slotToEdit);
    this.router.navigate(['/edit-scheduler-page']);
  }

  handlePlayruleSelected(playrule: Tempo) {
    this.hasUserMadeChanges = true;
    this.slotToEdit.tempo = playrule;
    this.slotToEdit.event = undefined;
  }

  handlePlaylistSelected(playlist: EventFamily) {
    this.hasUserMadeChanges = true;
    this.slotToEdit.event = playlist;
    this.slotToEdit.tempo = undefined;
  }

  onSlotTimePickerChanged($event: any) {
    this.hasUserMadeChanges = true;
    this.slotToEdit.startTime = [$event.beginningHour, $event.beginningMinute];
    this.slotToEdit.endTime = [$event.endHour, $event.endMinute];
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    // insert logic to check if there are pending changes here;
    // returning true will navigate without confirmation
    // returning false will show a confirm dialog before navigating away
    return !this.hasUserMadeChanges;
  }

  insertNewSlot(newSlot: TimeSlot) {
    // Suppression ou ajustement des slots recouverts
    this.handleOverlaps(newSlot);

    // Insérer le nouveau slot (la liste est déjà triée à ce point)
    const insertAt = this.slots.findIndex(slot => newSlot.startTime < slot.startTime);
    if (insertAt === -1) {
      this.slots.push(newSlot);
    } else {
      this.slots.splice(insertAt, 0, newSlot);
    }
  }

  private handleOverlaps(newSlot: TimeSlot) {
    for (let i = this.slots.length - 1; i >= 0; i--) {
      const slot = this.slots[i];
      if (this.timeAGreaterOrEquivalentToTimeB(slot.startTime, newSlot.startTime) && this.timeALessOrEquivalentToTimeB(slot.endTime, newSlot.endTime)) {
        // Slot entièrement recouvert, le supprimer
        this.slots.splice(i, 1);
      } else if (this.timeALessThanTimeB(slot.startTime, newSlot.startTime) && this.timeAGreaterThanTimeB(slot.endTime, newSlot.endTime)) {
        // Le nouveau slot se trouve au milieu de l'ancien, nécessitant un ajustement ou une division
        const afterSlot = {
          event: undefined,
          tempo: undefined,
          startTime: newSlot.endTime,
          endTime: slot.endTime
        };
        slot.endTime = newSlot.startTime;
        this.slots.splice(i + 1, 0, afterSlot);
      } else if (this.timeAGreaterThanTimeB(slot.endTime, newSlot.startTime) && this.timeALessOrEquivalentToTimeB(slot.endTime, newSlot.endTime)) {
        // Slot partiellement recouvert au début
        slot.endTime = newSlot.startTime;
        if (slot.event?.id === newSlot.event?.id || slot.tempo?.id === newSlot.tempo?.id) {
          slot.event = undefined;
          slot.tempo = undefined;
        }
      } else if (this.timeAGreaterOrEquivalentToTimeB(slot.startTime, newSlot.startTime) && this.timeALessThanTimeB(slot.startTime, newSlot.endTime)) {
        // Slot partiellement recouvert à la fin
        slot.startTime = newSlot.endTime;
        if (slot.event?.id === newSlot.event?.id || slot.tempo?.id === newSlot.tempo?.id) {
          slot.event = undefined;
          slot.tempo = undefined;
        }
      }
    }
  }

  private timeAGreaterThanTimeB(timeA: number[], timeB: number[]): boolean {
    return timeA[0] > timeB[0] || (timeA[0] === timeB[0] && timeA[1] > timeB[1]);
  }

  private timeAGreaterOrEquivalentToTimeB(timeA: number[], timeB: number[]): boolean {
    return timeA[0] > timeB[0] || (timeA[0] === timeB[0] && timeA[1] >= timeB[1]);
  }

  private timeALessThanTimeB(timeA: number[], timeB: number[]): boolean {
    return timeA[0] < timeB[0] || (timeA[0] === timeB[0] && timeA[1] < timeB[1]);
  }

  private timeALessOrEquivalentToTimeB(timeA: number[], timeB: number[]): boolean {
    return timeA[0] < timeB[0] || (timeA[0] === timeB[0] && timeA[1] <= timeB[1]);
  }

  onShuffleChange(value: boolean) {
    this.hasUserMadeChanges = true;
    this.slotToEdit.shuffle = value;
  }
}
